<script setup lang="ts">
    import { InputRow, DatePicker, Select, Combobox } from '@/components/ui/form';
    import { Input } from '@/components/ui/input';
    import ThousandSeparatorInput from '@/components/ThousandSeparatorInput.vue';
    import { computed } from 'vue';
    import type { Company, SelectOption, Employee } from '@/types';
    import { useI18n } from 'vue-i18n';
    import EmployeeEmploymentSalaryChangesTable from '@/components/employees/EmployeeEmploymentSalaryChangesTable.vue';

    const props = defineProps<{
        form: any;
        company: Company;
        employee?: Employee;
    }>();

    const { t, tm } = useI18n();

    const employmentTypeOptions = [
        { value: 'CustomerSalariedEmployee', label: 'employee.employment.salaried' },
        { value: 'CustomerHourlyEmployee', label: 'employee.employment.hourly' },
        { value: 'CustomerCEOEmployee', label: 'employee.employment.ceo' },
        // {
        //     value: 'CustomerBHonorariumReceiverEmployee',
        //     label: 'employee.employment.b_honorarium_receiver',
        // },
    ];

    const weeklyWorkingDaysOptions = Array.from({ length: 7 }, (_, i) => ({
        value: i + 1,
        label: i + 1,
    }));

    const taxCardOptions = [
        { value: 'hovedkort', label: 'employee.employment.primary_tax_card' },
        { value: 'bikort', label: 'employee.employment.secondary_tax_card' },
        { value: 'frikort', label: 'employee.employment.tax_exemption_card' },
    ];

    const allocationOfVacationDaysOptions = computed<SelectOption[]>(() => {
        const map = tm('employee.vacation.months') as Record<number, string>;
        return Object.entries(map).map(([value, label]) => ({ value: Number(value), label }));
    });

    const isSalaried = computed(
        () =>
            props.form.employment.employment_type === 'CustomerSalariedEmployee' ||
            props.form.employment.employment_type === 'CustomerCEOEmployee',
    );

    const isHourly = computed(
        () => props.form.employment.employment_type === 'CustomerHourlyEmployee',
    );

    const employeeOptional = computed(() => props.company?.employees_optional ?? {});
</script>

<template>
    <div class="grid gap-3">
        <InputRow
            :form="form"
            name="employment_type"
            label="employee.employment.employment_type"
        >
            <Select
                v-model="form.employment.employment_type"
                :options="employmentTypeOptions"
                translate
            />
        </InputRow>

        <div
            v-if="isSalaried"
            class="grid gap-3"
        >
            <InputRow
                :form="form"
                name="weekly_working_days"
                label="employee.employment.weekly_working_days"
                error-key="employment.weekly_working_days"
            >
                <Select
                    v-model="form.employment.weekly_working_days"
                    :options="weeklyWorkingDaysOptions"
                />
            </InputRow>

            <InputRow
                :form="form"
                name="weekly_working_hours"
                label="employee.employment.weekly_working_hours"
                error-key="employment.weekly_working_hours"
            >
                <ThousandSeparatorInput
                    id="weekly_working_hours"
                    v-model="form.employment.weekly_working_hours"
                    :placeholder="t('employee.employment.weekly_working_hours')"
                    with-decimals
                />
            </InputRow>

            <InputRow
                :form="form"
                name="salary"
                :label="
                    employee ? 'employee.employment.current_salary' : 'employee.employment.salary'
                "
                error-key="employment.salary"
            >
                <ThousandSeparatorInput
                    v-model="form.employment.salary"
                    :placeholder="
                        employee
                            ? t('employee.employment.current_salary')
                            : t('employee.employment.salary')
                    "
                    with-decimals
                    :disabled="employee"
                />
            </InputRow>

            <div
                v-if="employee"
                class="border-secondary grid gap-3 rounded-md border p-2.5"
            >
                <InputRow
                    :form="form"
                    name="salary"
                    label="employee.employment.new_salary"
                    error-key="employment.salary"
                >
                    <ThousandSeparatorInput
                        v-model="form.employment.new_salary"
                        :placeholder="t('employee.employment.new_salary')"
                        with-decimals
                    />
                </InputRow>
                <InputRow
                    :form="form"
                    name="new_salary_starts_at"
                    label="employee.employment.new_salary_starts_at"
                    error-key="employment.salary"
                >
                    <DatePicker v-model="form.employment.new_salary_starts_at" />
                </InputRow>
                <EmployeeEmploymentSalaryChangesTable
                    :employee="employee"
                    :form="form"
                />
            </div>
        </div>

        <InputRow
            v-if="isHourly"
            :form="form"
            name="hourly_rate"
            label="employee.employment.hourly_rate"
        >
            <ThousandSeparatorInput
                v-model="form.employment.hourly_rate"
                :placeholder="t('employee.employment.hourly_rate')"
                with-decimals
            />
        </InputRow>
        <InputRow
            :form="form"
            name="employment_date"
            label="employee.personal.employment_date"
            error-key="customer_employees.employment_date"
        >
            <DatePicker v-model="form.customer_employees.employment_date" />
        </InputRow>
        <InputRow
            :form="form"
            name="tax_card"
            label="employee.employment.tax_card"
            error-key="employment.tax_card"
        >
            <Select
                v-model="form.employment.tax_card"
                :options="taxCardOptions"
                translate
            />
        </InputRow>

        <InputRow
            v-if="employeeOptional.flex_savings"
            :form="form"
            name="flex_savings"
            label="employee.optional.flex_savings"
            error-key="optional.flex_savings"
        >
            <div class="relative">
                <Input
                    id="flex_savings"
                    v-model="form.optional.flex_savings"
                    :placeholder="t('employee.optional.flex_savings')"
                    type="number"
                    step="any"
                />
                <span
                    class="text-muted-foreground pointer-events-none absolute top-0 right-2.5 text-xs leading-7.5"
                >
                    %
                </span>
            </div>
        </InputRow>

        <InputRow
            v-if="employeeOptional.days_vacation"
            :form="form"
            name="days_vacation"
            label="employee.optional.days_vacation"
            error-key="optional.days_vacation"
        >
            <Input
                id="days_vacation"
                v-model="form.optional.days_vacation"
                :placeholder="t('employee.optional.days_vacation')"
                type="number"
                step="any"
            />
        </InputRow>
        <InputRow
            v-if="employeeOptional.days_vacation"
            :form="form"
            name="allocation_of_vacation_days"
            label="employee.optional.allocation_of_vacation_days"
        >
            <Combobox
                v-model="form.optional.allocation_of_vacation_days"
                :options="allocationOfVacationDaysOptions"
                :placeholder="t('employee.optional.allocation_of_vacation_days')"
                :show-remove-button="false"
                side="top"
            />
        </InputRow>
    </div>
</template>
